import { useEffect, useState, type ReactNode } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Blocks, FileStack } from "lucide-react";

type BuilderSubTab = "blocks" | "blueprints";

const SUB_TAB_KEY = (ws: string | undefined) =>
  `composer.shell.builder.tab.${ws ?? "anon"}`;

export type BuilderPanels = {
  blocks?: ReactNode;
  blueprints?: ReactNode;
};

type Props = {
  workspaceId?: string;
  panels?: BuilderPanels;
};

export const BuilderTabPanel = ({ workspaceId, panels }: Props) => {
  const [sub, setSub] = useState<BuilderSubTab>(() => {
    if (typeof window === "undefined") return "blocks";
    const stored = window.localStorage.getItem(SUB_TAB_KEY(workspaceId));
    // Migrate retired "templates" value → "blocks".
    return stored === "blueprints" ? "blueprints" : "blocks";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(SUB_TAB_KEY(workspaceId), sub);
  }, [sub, workspaceId]);

  return (
    <Tabs
      value={sub}
      onValueChange={(v) => setSub(v as BuilderSubTab)}
      className="flex h-full flex-col"
    >
      <div className="shrink-0 border-b border-border/60 p-2">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="blocks" className="gap-1.5">
            <Blocks className="h-3.5 w-3.5" />
            <span>Blocks</span>
          </TabsTrigger>
          <TabsTrigger value="blueprints" className="gap-1.5">
            <FileStack className="h-3.5 w-3.5" />
            <span>Blueprints</span>
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="blocks" className="mt-0 min-h-0 flex-1 overflow-auto">
        {panels?.blocks}
      </TabsContent>
      <TabsContent value="blueprints" className="mt-0 min-h-0 flex-1 overflow-auto">
        {panels?.blueprints}
      </TabsContent>
    </Tabs>
  );
};

export default BuilderTabPanel;
